import { GeneralLink } from "@customTypes/generalLink";
import clsx from "clsx";
import React, { useEffect, useRef } from "react";
import Arrow from "../../public/assets2019/img/svg/arrow-carousel.svg";
import styles from "./C03.1_SubNavigationDE.module.scss";
import NavLink from "./NavLink";

interface NavDropdownProps {
  isOpen: boolean;
  id: string;
  secondaryNavLinks: GeneralLink[];
  dropdownLinkHref: string;
  dropdownLinkText: string;
  onToggle: (id: string) => void;
  onNavClick: (
    e:
      | React.MouseEvent<HTMLAnchorElement>
      | React.KeyboardEvent<HTMLAnchorElement>
  ) => void;
  onFirstLevelKeyDown: (
    e: React.KeyboardEvent<HTMLAnchorElement | HTMLButtonElement>
  ) => void;
  activeURL: string;
}

const NavDropdown: React.FC<NavDropdownProps> = ({
  id,
  isOpen,
  secondaryNavLinks,
  dropdownLinkHref,
  dropdownLinkText,
  onNavClick,
  onToggle,
  onFirstLevelKeyDown,
  activeURL,
}) => {
  const subNavWrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null); // Add ref for toggle button
  const buttonId = `submenu-button-${id.replace(/ /g, "")}`;
  const submenuId = `submenu-${id.replace(/ /g, "")}`;

  const handleArrowClick = () => {
    onToggle(id);
  };

  const handleToggleButtonKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>
  ) => {
    if (isOpen) {
      if (event.key === "ArrowDown" || event.key === "ArrowRight") {
        event.preventDefault();

        const anchors =
          listRef.current?.querySelectorAll<HTMLAnchorElement>("a");
        if (anchors && anchors.length > 0) {
          anchors[0].focus();
        }
      }
    } else {
      onFirstLevelKeyDown(event);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLAnchorElement>) => {
    const { key } = event;
    const anchors = listRef.current?.querySelectorAll<HTMLAnchorElement>("a");
    if (!anchors || anchors.length === 0) return;

    const currentIndex = Array.from(anchors).findIndex(
      (anchor) => anchor === event.currentTarget
    );

    switch (key) {
      case "Escape":
        event.preventDefault();
        onToggle(id);
        buttonRef.current?.focus();
        break;

      case "ArrowDown":
      case "ArrowRight":
        event.preventDefault();
        if (currentIndex < anchors.length - 1) {
          anchors[currentIndex + 1].focus();
        }
        break;

      case "ArrowUp":
      case "ArrowLeft":
        event.preventDefault();
        if (currentIndex > 0) {
          anchors[currentIndex - 1].focus();
        }
        break;

      default:
    }
  };

  // Focus boundary: close submenu if focus moves outside button and submenu
  useEffect(() => {
    if (!isOpen) return;

    function onFocusIn(event: FocusEvent) {
      const target = event.target as Node;
      if (
        buttonRef.current &&
        subNavWrapperRef.current &&
        !buttonRef.current.contains(target) &&
        !subNavWrapperRef.current.contains(target)
      ) {
        // Focus moved outside toggle button and submenu — close submenu
        onToggle(id);
      }
    }

    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [isOpen, id, onToggle]);

  const onBackLinkClick = () => {
    onToggle(id);
  };

  return (
    <li
      className={clsx(
        styles["dropdown"],
        styles["subnav__item"],
        isOpen && styles.open
      )}
    >
      <NavLink
        url={dropdownLinkHref}
        customClass={styles["dropdown__link"]}
        linkText={dropdownLinkText}
        onNavClick={onNavClick}
        isActive={dropdownLinkHref === activeURL}
        onNavKeyDown={onFirstLevelKeyDown}
      />
      <button
        ref={buttonRef}
        id={buttonId}
        aria-expanded={isOpen}
        aria-haspopup
        aria-controls={submenuId}
        aria-label={`${dropdownLinkText}: submenu`}
        className={clsx(styles["dropdown__toggle"])}
        onClick={handleArrowClick}
        onKeyDown={handleToggleButtonKeyDown}
      >
        <Arrow aria-hidden />
      </button>
      <div
        className={clsx(
          styles["dropdown__submenu"],
          isOpen && styles["dropdown__submenu--open"],
          !isOpen && styles.hidden
        )}
        ref={subNavWrapperRef}
        id={submenuId}
      >
        <div className={clsx(styles["dropdown__wrapper"], "wrapper")}>
          <button
            onClick={onBackLinkClick}
            className={clsx(styles["dropdown__back-button"], "md:hidden")}
            tabIndex={isOpen ? 0 : -1}
          >
            <Arrow aria-hidden className={styles["dropdown__back-icon"]} />
            Back
          </button>
          <ul
            className={clsx(styles["dropdown__list"])}
            ref={listRef}
            aria-labelledby={buttonId}
          >
            {secondaryNavLinks.map((dropdownLink, i) => (
              <li
                key={`${i}${dropdownLink.fields?.linkText}`}
                className={styles["dropdown__item"]}
              >
                <NavLink
                  {...dropdownLink.fields}
                  onNavClick={onNavClick}
                  onNavKeyDown={handleKeyDown}
                  tabIndex={isOpen ? 0 : -1}
                  isActive={dropdownLink.fields?.url === activeURL}
                />
              </li>
            ))}
          </ul>
        </div>
      </div>
    </li>
  );
};

NavDropdown.displayName = "NavDropdown";

export default NavDropdown;
